import type { CSSProperties, ReactNode } from "react";

/**
 * Original illustration for recipes without a photo: an enamel plate seen from above on a tiled
 * wall, with a motif chosen from the recipe's own words. Deterministic per recipe; token colours only.
 */
type Motif = "bowl" | "loaf" | "leaf" | "fish" | "cup" | "noodles" | "egg" | "initial";

const MOTIFS: Array<[Motif, string[]]> = [
  ["cup", ["drink", "smoothie", "coffee", "tea", "lemonade", "cocktail", "juice", "latte", "shake", "punch", "cider", "cocoa"]],
  ["noodles", ["pasta", "spaghetti", "noodle", "linguine", "fettuccine", "penne", "macaroni", "udon", "lo mein", "carbonara", "bolognese"]],
  ["egg", ["egg", "frittata", "omelet", "omelette", "shakshuka", "breakfast", "quiche", "benedict"]],
  ["fish", ["fish", "salmon", "cod", "tuna", "seafood", "shrimp", "prawn", "halibut", "trout", "mackerel", "sardine", "crab"]],
  ["loaf", ["bread", "loaf", "cake", "muffin", "cookie", "pie", "tart", "bake", "brownie", "scone", "bun", "biscuit", "focaccia", "dessert", "pastry", "cobbler", "crumble"]],
  ["bowl", ["soup", "stew", "curry", "chili", "ramen", "pho", "chowder", "broth", "dal", "dahl", "porridge", "oat", "risotto", "congee", "gumbo"]],
  ["leaf", ["salad", "greens", "slaw", "vegetable", "veg", "kale", "spinach", "herb", "pesto", "vegan", "bowl"]],
];

function hash(text: string) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function pickMotif(title: string, categories: string[] = []): Motif {
  const text = ` ${[title, ...categories].join(" ").toLowerCase()} `;
  for (const [motif, words] of MOTIFS) {
    if (words.some((word) => text.includes(word))) return motif;
  }
  return "initial";
}

const WALLS = ["tile-soft", "pistachio-soft", "butter-soft", "page-deep"] as const;

const c = (name: string): CSSProperties => ({ fill: `var(--color-${name})` });
const s = (name: string, width = 4): CSSProperties => ({ stroke: `var(--color-${name})`, strokeWidth: width, fill: "none", strokeLinecap: "round", strokeLinejoin: "round" });
const fs = (fill: string, stroke = "rim", width = 4): CSSProperties => ({ fill: `var(--color-${fill})`, stroke: `var(--color-${stroke})`, strokeWidth: width, strokeLinejoin: "round" });

function MotifArt({ motif, initial }: { motif: Motif; initial: string }): ReactNode {
  switch (motif) {
    case "bowl":
      return (
        <g>
          <circle cx="200" cy="150" r="66" style={fs("butter", "rim", 3)} />
          <circle cx="200" cy="150" r="54" style={c("butter-soft")} opacity="0.55" />
          <ellipse cx="176" cy="132" rx="12" ry="6" transform="rotate(-30 176 132)" style={c("pistachio-deep")} />
          <ellipse cx="214" cy="170" rx="11" ry="5" transform="rotate(25 214 170)" style={c("pistachio-deep")} />
          <ellipse cx="226" cy="128" rx="9" ry="4.5" transform="rotate(60 226 128)" style={c("pistachio")} />
          <circle cx="186" cy="168" r="5" style={c("cherry")} />
          <path d="M268 88 L318 40" style={s("rim", 9)} />
          <ellipse cx="258" cy="100" rx="16" ry="11" transform="rotate(-45 258 100)" style={fs("enamel", "rim", 4)} />
        </g>
      );
    case "loaf":
      return (
        <g>
          <path d="M132 176 C132 118 160 104 200 104 C240 104 268 118 268 176 Q268 190 254 190 L146 190 Q132 190 132 176 Z" style={fs("butter", "rim", 4)} />
          <path d="M162 140 L178 124 M192 142 L208 124 M222 142 L238 126" style={s("butter-deep", 5)} />
          <path d="M140 172 Q200 180 260 172" style={s("butter-deep", 3)} opacity="0.5" />
        </g>
      );
    case "leaf":
      return (
        <g>
          <path d="M150 170 C150 120 190 100 226 104 C230 142 206 176 150 170 Z" style={fs("pistachio", "rim", 3.5)} />
          <path d="M156 164 L218 112" style={s("rim", 2.5)} />
          <path d="M244 184 C214 176 204 140 222 118 C252 126 262 160 244 184 Z" style={fs("pistachio-deep", "rim", 3.5)} />
          <path d="M241 178 L226 126" style={s("pistachio-soft", 2.5)} />
          <circle cx="178" cy="190" r="13" style={fs("cherry", "rim", 3)} />
          <circle cx="206" cy="196" r="10" style={fs("cherry", "rim", 3)} />
          <circle cx="172" cy="186" r="3" style={c("enamel")} />
        </g>
      );
    case "fish":
      return (
        <g>
          <path d="M126 150 C150 116 214 110 250 150 C214 190 150 184 126 150 Z" style={fs("tile-soft", "rim", 4)} />
          <path d="M250 150 L284 124 L278 150 L284 176 Z" style={fs("tile", "rim", 4)} />
          <circle cx="152" cy="144" r="5" style={c("rim")} />
          <path d="M178 132 Q188 150 178 168 M200 128 Q212 150 200 172" style={s("tile", 3)} />
          <circle cx="244" cy="206" r="20" style={fs("butter", "rim", 3)} />
          <path d="M244 188 V224 M226 206 H262 M231 193 L257 219 M257 193 L231 219" style={s("butter-deep", 1.5)} />
        </g>
      );
    case "cup":
      return (
        <g>
          <circle cx="196" cy="150" r="58" style={fs("enamel", "rim", 5)} />
          <circle cx="196" cy="150" r="44" style={c("butter-deep")} opacity="0.9" />
          <path d="M178 140 Q196 128 214 140 Q196 152 178 140 Z" style={c("butter-soft")} opacity="0.85" />
          <path d="M254 136 Q284 136 284 158 Q284 180 252 170" style={s("rim", 8)} />
        </g>
      );
    case "noodles":
      return (
        <g>
          <path d="M150 152 C150 118 196 112 212 132 C228 152 196 176 178 162 C160 148 186 128 204 142" style={s("butter", 9)} />
          <path d="M150 152 C150 118 196 112 212 132 C228 152 196 176 178 162 C160 148 186 128 204 142" style={s("butter-deep", 2)} opacity="0.35" />
          <path d="M236 128 C262 142 262 178 226 190 C196 200 160 190 150 170" style={s("butter", 9)} />
          <circle cx="232" cy="160" r="8" style={fs("cherry", "rim", 2.5)} />
          <circle cx="172" cy="188" r="7" style={fs("cherry", "rim", 2.5)} />
          <path d="M214 176 C218 160 234 156 244 164 C236 176 224 180 214 176 Z" style={fs("pistachio", "rim", 2.5)} />
        </g>
      );
    case "egg":
      return (
        <g>
          <path d="M146 148 C140 112 184 98 212 112 C246 104 270 136 254 164 C262 196 214 208 190 196 C160 206 132 180 146 148 Z" style={fs("enamel", "rim", 3.5)} />
          <circle cx="202" cy="152" r="26" style={fs("butter", "butter-deep", 3)} />
          <circle cx="194" cy="144" r="7" style={c("butter-soft")} />
          <circle cx="252" cy="208" r="3" style={c("rim")} />
          <circle cx="262" cy="200" r="2.5" style={c("rim")} />
          <circle cx="258" cy="216" r="2" style={c("rim")} />
        </g>
      );
    case "initial":
      return (
        <g>
          <text x="200" y="150" textAnchor="middle" dominantBaseline="central" className="recipe-art__initial">
            {initial}
          </text>
          <path d="M112 212 L112 238 M104 212 L104 226 M120 212 L120 226 M104 226 Q112 234 120 226" style={s("line-strong", 3)} />
          <path d="M288 210 Q300 216 296 238" style={s("line-strong", 3)} />
        </g>
      );
  }
}

export function RecipeArt({ seed, title, categories = [], className, decorative = true }: { seed: string; title: string; categories?: string[]; className?: string; decorative?: boolean }) {
  const h = hash(seed || title);
  const wall = WALLS[h % WALLS.length];
  const motif = pickMotif(title, categories);
  const initial = (title.trim()[0] ?? "S").toUpperCase();
  const tilt = (h % 7) - 3;
  return (
    <svg
      className={["recipe-art", className ?? ""].join(" ")}
      viewBox="0 0 400 300"
      preserveAspectRatio="xMidYMid slice"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : `Illustration for ${title}`}
    >
      <rect width="400" height="300" style={c(wall)} />
      <g opacity="0.55" style={s("enamel", 2)}>
        {[60, 140, 220].map((y) => (
          <path key={`h${y}`} d={`M0 ${y} H400`} />
        ))}
        {[0, 80, 160, 240, 320, 400].map((x, i) => (
          <path key={`v${x}`} d={`M${x + (i % 2 ? 40 : 0)} 0 V300`} />
        ))}
      </g>
      <g transform={`rotate(${tilt} 200 150)`}>
        <ellipse cx="204" cy="160" rx="118" ry="112" style={c("rim")} opacity="0.12" />
        <circle cx="200" cy="150" r="112" style={fs("enamel", "rim", 5)} />
        <circle cx="200" cy="150" r="84" style={s("line", 2)} />
        <MotifArt motif={motif} initial={initial} />
      </g>
    </svg>
  );
}
